import React from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { selectCurrentCluster } from "../../../store/slices/currentClusterSlice";
import { ROUTES } from "../../utils/constants";
import { useTheme } from "../../../common/hooks/useTheme";
import useMetrics from "../../../common/hooks/useMetrics";
import { GoalType } from "../../../common/types/enums";

import styles from "./Footer.module.scss";
import { formatPhoneNumber } from "../../utils/functions/formatPhoneNumber";

// Импорты кнопок магазинов
import appStoreDark from "../../../assets/app-stores/app_store_dark.svg";
import appStoreLight from "../../../assets/app-stores/app_store_light.svg";
import ruStoreDark from "../../../assets/app-stores/ru_store_dark.svg";
import ruStoreLight from "../../../assets/app-stores/ru_store_light.svg";
import { StoreButton } from "../storeButton/StoreButton";
import { ApkButton } from "../apkButton/ApkButton";
import { getClientConfig } from "../../../config/runtimeConfig";

export const Footer = () => {
  const currentCluster = useSelector(selectCurrentCluster);
  const { isDark } = useTheme();
  const { trackGoal } = useMetrics();

  const showUs = getClientConfig().showUs;

  const phone = currentCluster?.phone_number;
  const brandName = currentCluster?.name || "Компания";
  const legalName = currentCluster?.legalName;
  const legalInn = currentCluster?.legalInn;
  const isQsoil = currentCluster?.id === 7;

  // Логотип и ссылка для Qsoil
  const logoSrc = isQsoil ? "/icons/qs_bb.webp" : "/icons/logo192.png";
  const logoLink = isQsoil ? "https://qsgrp.com/" : undefined;

  // ✅ Все ссылки из .env
  const appStoreUrl = process.env.REACT_APP_APPSTORE_URL;
  const appGalleryUrl = process.env.REACT_APP_APPGALLERY_URL;
  const ruStoreUrl = process.env.REACT_APP_RUSTORE_URL;
  const directDownloadUrl = process.env.REACT_APP_DIRECT_DOWNLOAD_URL;

  // Выбор цвета кнопок в зависимости от темы
  const appStoreBadge = isDark ? appStoreLight : appStoreDark;
  const ruStoreBadge = isDark ? ruStoreLight : ruStoreDark;

  const handlePhoneClick = (phone: string) => {
    trackGoal(GoalType.CALL_CLICK, phone);
  };

  return (
    <footer className={styles.footer}>
      <div className={styles.container}>
        <div className={styles.flexContainer}>
          {/* Колонка с логотипом */}
          <div className={styles.logoColumn}>
            {logoLink ? (
              <a href={logoLink} target="_blank" rel="noopener noreferrer">
                <img src={logoSrc} alt={brandName} className={isQsoil ? styles.logoCustom : styles.logo} />
              </a>
            ) : (
              <img src={logoSrc} alt={brandName} className={styles.logo} />
            )}
          </div>

          {/* Колонка: Скачать приложение */}
          <div className={styles.column}>
            <h3 className={styles.columnTitle}>Скачать приложение</h3>
            <ul className={styles.apps}>
              {/* App Store */}
              {appStoreUrl && (
                <li>
                  <StoreButton
                    href={appStoreUrl}
                    src={appStoreBadge}
                    alt="Скачать в App Store"
                  />
                </li>
              )}

              {/* RuStore */}
              {ruStoreUrl && (
                <li>
                  <StoreButton
                    href={ruStoreUrl}
                    src={ruStoreBadge}
                    alt="Скачать в RuStore"
                  />
                </li>
              )}

              {/* AppGallery - текстовая ссылка (нет бейджа) */}
              {appGalleryUrl && (
                <li>
                  <a
                    href={appGalleryUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.link}
                  >
                    AppGallery
                  </a>
                </li>
              )}

              {/* Прямая ссылка для Android - используем ApkButton */}
              {directDownloadUrl && (
                <li>
                  <ApkButton downloadUrl={directDownloadUrl} />
                </li>
              )}
            </ul>
          </div>

          {/* Колонка: Правовая информация */}
          <div className={styles.column}>
            <h3 className={styles.columnTitle}>Правовая информация</h3>
            <ul className={styles.links}>
              <li>
                <Link to={ROUTES.USER_AGREEMENT} className={styles.link}>
                  Пользовательское соглашение
                </Link>
              </li>
              <li>
                <Link to={ROUTES.PRIVACY_POLICY} className={styles.link}>
                  Политика конфиденциальности
                </Link>
              </li>
              <li>
                <Link
                  to={ROUTES.PERSONAL_DATA_AGREEMENT}
                  className={styles.link}
                >
                  Согласие на обработку данных
                </Link>
              </li>
              {isQsoil && (
                <li>
                  <Link to={ROUTES.COOKIE_POLICY} className={styles.link}>
                    Политика использования cookie
                  </Link>
                </li>
              )}
            </ul>
          </div>

          {/* Колонка: Контакты */}
          <div className={styles.column}>
            <h3 className={styles.columnTitle}>Контакты</h3>
            {phone && (
              <a
                href={`tel:${phone}`}
                className={styles.phone}
                onClick={() => handlePhoneClick(phone)}
              >
                {formatPhoneNumber(phone)}
              </a>
            )}
          </div>
        </div>

        {/* Нижняя часть с копирайтом */}
        <div className={styles.bottom}>
          <div className={styles.copyright}>
            © {new Date().getFullYear()} {brandName}
          </div>

          {isQsoil ? (
            <div className={styles.legalInfo}>
              Технические и технологические решения для горнодобывающей отрасли:
              проектирование, оборудование, аренда, сервис, запасные части.
              <br />
              Исключительные права на все материалы, опубликованные на данном
              сайте в любом виде, принадлежат{" "}
              {legalName || "ООО «Карьер-Сервис»"}.
            </div>
          ) : (
            (legalName || legalInn) && (
              <div className={styles.legalInfo}>
                {legalName} {legalInn && `• ИНН ${legalInn}`}
              </div>
            )
          )}

          {showUs && (
            <>
              <div className={styles.studioLink}>
                Сайт сделан в{" "}
                <a
                  href="https://seacoding.ru"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  СИ КОДИНГ | SEA CODING
                </a>
              </div>
              <div className={styles.studioLink}>
                Платформа{" "}
                <a
                  href="https://logid.ru"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  ЛОГАЙДИ | LOGID
                </a>
              </div>
            </>
          )}
        </div>
      </div>
    </footer>
  );
};
